/**
 * OFC Variant Configurations
 *
 * Defines the rules and parameters for each OFC variant:
 * - Standard OFC: Sequential play, 1 card per round for 8 rounds
 * - Pineapple OFC: Sequential play, 3 cards dealt, place 2, discard 1 for 4 rounds
 * - 2-7 OFC: Lowball scoring for middle row
 * - Joker OFC: Includes wild joker cards
 *
 * Note: All variants use sequential acting (one player at a time).
 */
import { ActingMode, OFCVariant, Rank } from './enums';
import { FantasyConfig, OFCVariantConfig } from './types';

/** Standard fantasy: QQ+ entry, no progressive card counts */
const STANDARD_FANTASY: FantasyConfig = {
  enabled: true,
  entryMinRank: Rank.QUEEN,
  allows27MiddleEntry: false,
  progressiveCardCounts: false,
};

/** Progressive fantasy: QQ+ entry, card count scales with hand strength */
const PROGRESSIVE_FANTASY: FantasyConfig = {
  enabled: true,
  entryMinRank: Rank.QUEEN,
  allows27MiddleEntry: false,
  progressiveCardCounts: true,
};

/** 2-7 fantasy: KK+ or perfect middle entry */
const DEUCE_TO_SEVEN_FANTASY: FantasyConfig = {
  enabled: true,
  entryMinRank: Rank.KING,
  allows27MiddleEntry: true,
  progressiveCardCounts: false,
};

/** Standard OFC: 5 initial cards, then 1 card per round for 8 rounds */
export const STANDARD_OFC: OFCVariantConfig = {
  name: 'Open Face Chinese',
  variant: OFCVariant.OpenFaceChineseStandard,
  code: 'OFC',
  maxPlayers: 3,
  actingMode: ActingMode.SEQUENTIAL,
  revealAfterRound: true,
  initialCards: 5,
  cardsPerRound: 1,
  cardsToPlacePerRound: 1,
  cardsToDiscardPerRound: 0,
  totalRounds: 8,
  fantasy: null,
};

/** Pineapple OFC: 5 initial cards, then 3 cards per round (place 2, discard 1) */
export const PINEAPPLE_OFC: OFCVariantConfig = {
  name: 'Pineapple OFC',
  variant: OFCVariant.OpenFaceChinesePineapple,
  code: 'OFC-P',
  maxPlayers: 3,
  actingMode: ActingMode.SIMULTANEOUS,
  revealAfterRound: true,
  initialCards: 5,
  cardsPerRound: 3,
  cardsToPlacePerRound: 2,
  cardsToDiscardPerRound: 1,
  totalRounds: 4,
  fantasy: STANDARD_FANTASY,
};

/** Pineapple OFC with Progressive Fantasy (QQ=14, KK=15, AA=16, Trips=17) */
export const PINEAPPLE_FANTASY_OFC: OFCVariantConfig = {
  name: 'Pineapple OFC Fantasy',
  variant: OFCVariant.OpenFaceChinesePineappleFantasy,
  code: 'OFC-PF',
  maxPlayers: 3,
  actingMode: ActingMode.SIMULTANEOUS,
  revealAfterRound: true,
  initialCards: 5,
  cardsPerRound: 3,
  cardsToPlacePerRound: 2,
  cardsToDiscardPerRound: 1,
  totalRounds: 4,
  fantasy: PROGRESSIVE_FANTASY,
};

/** Standard 2-7 OFC (no fantasy) */
export const DEUCE_TO_SEVEN_OFC: OFCVariantConfig = {
  name: '2-7 OFC',
  variant: OFCVariant.OpenFaceChineseDeuceToSeven,
  code: 'OFC-27',
  maxPlayers: 3,
  actingMode: ActingMode.SEQUENTIAL,
  revealAfterRound: true,
  initialCards: 5,
  cardsPerRound: 1,
  cardsToPlacePerRound: 1,
  cardsToDiscardPerRound: 0,
  totalRounds: 8,
  fantasy: null,
};

/** 2-7 Pineapple OFC with fantasy (KK+ or perfect middle) */
export const DEUCE_TO_SEVEN_PINEAPPLE_OFC: OFCVariantConfig = {
  name: '2-7 Pineapple OFC',
  variant: OFCVariant.OpenFaceChinesePineappleDeuceToSeven,
  code: 'OFC-P27',
  maxPlayers: 3,
  actingMode: ActingMode.SIMULTANEOUS,
  revealAfterRound: true,
  initialCards: 5,
  cardsPerRound: 3,
  cardsToPlacePerRound: 2,
  cardsToDiscardPerRound: 1,
  totalRounds: 4,
  fantasy: DEUCE_TO_SEVEN_FANTASY,
};

/** Joker OFC (no fantasy) */
export const JOKER_OFC: OFCVariantConfig = {
  name: 'Joker OFC',
  variant: OFCVariant.OpenFaceChineseJoker,
  code: 'OFC-J',
  maxPlayers: 3,
  actingMode: ActingMode.SEQUENTIAL,
  revealAfterRound: true,
  initialCards: 5,
  cardsPerRound: 1,
  cardsToPlacePerRound: 1,
  cardsToDiscardPerRound: 0,
  totalRounds: 8,
  fantasy: null,
};

/** Joker Pineapple OFC with progressive fantasy */
export const JOKER_PINEAPPLE_OFC: OFCVariantConfig = {
  name: 'Joker Pineapple OFC',
  variant: OFCVariant.OpenFaceChinesePineappleJoker,
  code: 'OFC-PJ',
  maxPlayers: 3,
  actingMode: ActingMode.SIMULTANEOUS,
  revealAfterRound: true,
  initialCards: 5,
  cardsPerRound: 3,
  cardsToPlacePerRound: 2,
  cardsToDiscardPerRound: 1,
  totalRounds: 4,
  fantasy: PROGRESSIVE_FANTASY,
};

const OFC_VARIANT_REGISTRY: Map<OFCVariant, OFCVariantConfig> = new Map([
  [OFCVariant.OpenFaceChineseStandard, STANDARD_OFC],
  [OFCVariant.OpenFaceChinesePineapple, PINEAPPLE_OFC],
  [OFCVariant.OpenFaceChinesePineappleFantasy, PINEAPPLE_FANTASY_OFC],
  [OFCVariant.OpenFaceChineseDeuceToSeven, DEUCE_TO_SEVEN_OFC],
  [OFCVariant.OpenFaceChinesePineappleDeuceToSeven, DEUCE_TO_SEVEN_PINEAPPLE_OFC],
  [OFCVariant.OpenFaceChineseJoker, JOKER_OFC],
  [OFCVariant.OpenFaceChinesePineappleJoker, JOKER_PINEAPPLE_OFC],
]);

/** Get configuration for a variant */
export function getVariantConfig(variant: OFCVariant): OFCVariantConfig {
  const config = OFC_VARIANT_REGISTRY.get(variant);
  if (!config) {
    throw new Error(`Unknown OFC variant: ${variant}`);
  }
  return config;
}

/** Get number of cards dealt for a given round (initial vs subsequent) */
export function getCardsForRound(config: OFCVariantConfig, roundIndex: number): number {
  return roundIndex === 0 ? config.initialCards : config.cardsPerRound;
}

/** Get number of cards to place for a given round */
export function getCardsToPlace(config: OFCVariantConfig, roundIndex: number): number {
  return roundIndex === 0 ? config.initialCards : config.cardsToPlacePerRound;
}

/** Get number of cards to discard for a given round */
export function getCardsToDiscard(config: OFCVariantConfig, roundIndex: number): number {
  return roundIndex === 0 ? 0 : config.cardsToDiscardPerRound;
}

/** Check if variant uses pineapple rules (3 cards dealt, place 2, discard 1) */
export function isPineappleVariant(variant: OFCVariant): boolean {
  const config = getVariantConfig(variant);
  return config.cardsPerRound === 3;
}

/** Check if variant uses 2-7 lowball scoring for middle row */
export function isDeuceToSevenVariant(variant: OFCVariant): boolean {
  return (
    variant === OFCVariant.OpenFaceChineseDeuceToSeven || variant === OFCVariant.OpenFaceChinesePineappleDeuceToSeven
  );
}

/** Check if variant includes joker wild cards */
export function isJokerVariant(variant: OFCVariant): boolean {
  return variant === OFCVariant.OpenFaceChineseJoker || variant === OFCVariant.OpenFaceChinesePineappleJoker;
}

/** Check if variant has fantasy mode enabled */
export function hasFantasy(variant: OFCVariant): boolean {
  const config = getVariantConfig(variant);
  return config.fantasy?.enabled ?? false;
}
